import React, { useState } from "react";

//MUI components
import {
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    CircularProgress,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

//Icons
import CloseIcon from "@material-ui/icons/Close";
import { ReactComponent as IconCSV } from "../../../Icons/csv-icon.svg";

//Custom portal components
import CustomButton from "../../common/CustomButton";

//External libraries
import { DropzoneAreaBase } from "material-ui-dropzone";
import Swal from "sweetalert2";

const Dialog = withStyles(() => ({
    paper: {
        borderRadius: 12,
        width: "100%",
        maxWidth: 520,
    },
}))(MuiDialog);

const DialogTitle = withStyles(() => ({
    root: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #e3e3e3",
        padding: "12px 20px",
    },
}))(MuiDialogTitle);

const acceptedFiles = [".csv"];

// Campos del contacto que el servicio espera. Se usan como posibles encabezados
// del CSV; también se aceptan alias en español por comodidad del usuario.
const headerAliases = {
    phone_number: "phone_number",
    "numero de telefono": "phone_number",
    "número de teléfono": "phone_number",
    telefono: "phone_number",
    teléfono: "phone_number",
    name: "name",
    nombre: "name",
    gender: "gender",
    genero: "gender",
    género: "gender",
    age: "age",
    edad: "age",
    email: "email",
    correo: "email",
    state: "state",
    estado: "state",
    "entidad federativa": "state",
    locality: "locality",
    localidad: "locality",
    interests: "interests",
    intereses: "interests",
};

// Normaliza el valor de género a "F"/"M" (acepta variantes en español/inglés).
const normalizeGender = (raw) => {
    const g = (raw ?? "").toString().trim().toLowerCase();
    if (["f", "female", "femenino"].includes(g)) return "F";
    if (["m", "male", "masculino"].includes(g)) return "M";
    return "";
};

/**
 * Convierte el texto de un CSV a un arreglo de contactos (objetos JSON).
 * - La primera línea son los encabezados (separados por coma).
 * - Los intereses múltiples se capturan en una sola columna separados por ";".
 *   Ej: "campaña;whatsapp;promociones" -> ["campaña", "whatsapp", "promociones"]
 */
const convertCSVtoContacts = (text) => {
    const delimiter = ",";
    const firstBreak = text.indexOf("\n");
    if (firstBreak === -1) return [];

    const rawHeaders = text.slice(0, firstBreak).split(delimiter);
    const headers = rawHeaders
        .map((h) => h.replace(/(\r\n|\n|\r)/gm, "").trim().toLowerCase())
        .map((h) => headerAliases[h] || h);

    const rows = text
        .slice(firstBreak + 1)
        .split("\n")
        .map((row) => row.replace(/(\r\n|\n|\r)/gm, ""))
        .filter((row) => row.trim());

    return rows.map((row) => {
        const values = row.split(delimiter);
        const contact = {};
        headers.forEach((header, index) => {
            const value = values[index] ? values[index].trim() : "";
            switch (header) {
                case "age":
                    // Edad numérica (o null si viene vacía).
                    contact.age = value === "" ? null : Number(value);
                    break;
                case "gender":
                    contact.gender = normalizeGender(value);
                    break;
                case "interests":
                    // Intereses separados por ";" -> arreglo.
                    contact.interests = value
                        ? value.split(";").map((i) => i.trim()).filter(Boolean)
                        : [];
                    break;
                default:
                    contact[header] = value;
                    break;
            }
        });
        return contact;
    });
};

/**
 * Modal para importar un archivo (CSV) con varios contactos.
 * El portal lee el CSV, lo convierte a un arreglo de contactos y se lo pasa a
 * onImport(contactsArray) para enviarlo al endpoint de agregar contacto.
 * - onClose(): cierra el modal
 * - onImport(contactsArray): envía los contactos; debe devolver true si tuvo éxito
 */
const ImportContactsModal = (props) => {
    const { open, onClose, onImport } = props;
    const [fileObjects, setFileObjects] = useState([]);
    const [importing, setImporting] = useState(false);

    const resetAndClose = () => {
        setFileObjects([]);
        onClose();
    };

    const handleImport = async () => {
        if (fileObjects.length === 0) return;
        setImporting(true);
        try {
            // 1. Leer el CSV como texto en el navegador.
            const text = await fileObjects[0].file.text();
            // 2. Convertir a arreglo de contactos.
            const contactsArray = convertCSVtoContacts(text);

            if (contactsArray.length === 0) {
                setImporting(false);
                Swal.fire({
                    icon: "error",
                    title: "El archivo está vacío",
                    text: "No se encontraron contactos en el archivo CSV.",
                });
                return;
            }

            // 3. Enviar el arreglo al endpoint de agregar contacto.
            const ok = await onImport(contactsArray);
            setImporting(false);
            if (ok) {
                setFileObjects([]);
                onClose();
            }
        } catch (error) {
            setImporting(false);
            Swal.fire({
                icon: "error",
                title: "No se pudo leer el archivo",
                text: "Verifica que el formato CSV sea válido e intenta nuevamente.",
            });
        }
    };

    return (
        <Dialog open={open} onClose={importing ? undefined : resetAndClose}>
            <DialogTitle disableTypography>
                <Typography variant="h6" style={{ color: "#27315D", fontWeight: 600 }}>
                    Importar contactos
                </Typography>
                <IconButton size="small" onClick={resetAndClose} disabled={importing}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ paddingTop: 20 }}>
                <Typography variant="body2" style={{ color: "#6b6b6b", marginBottom: 16 }}>
                    Sube un archivo CSV con los contactos que deseas importar.
                </Typography>
                <DropzoneAreaBase
                    fileObjects={fileObjects}
                    filesLimit={1}
                    maxFileSize={4000000} //4mb
                    acceptedFiles={acceptedFiles}
                    showFileNames
                    onAdd={(newFileObjects) => setFileObjects(newFileObjects)}
                    onDelete={() => setFileObjects([])}
                    dropzoneText={fileObjects[0] ? "" : "Arrastra el archivo CSV o haz clic para seleccionarlo"}
                    getPreviewIcon={() => (
                        <div style={{ display: "flex", justifyContent: "center", width: "50px" }}>
                            <IconCSV className="icon-primary-color" />
                        </div>
                    )}
                    showAlerts={["error", "info"]}
                    alertSnackbarProps={{
                        anchorOrigin: { vertical: "top", horizontal: "right" },
                    }}
                    getDropRejectMessage={(rejectedFile) =>
                        `El archivo ${rejectedFile.name} no es compatible o excede el máximo permitido de 4MB.`
                    }
                />
            </DialogContent>
            <DialogActions style={{ padding: "12px 20px" }}>
                <CustomButton
                    onClick={handleImport}
                    disabled={fileObjects.length === 0 || importing}
                    variant="contained"
                >
                    {importing ? (
                        <CircularProgress size={20} style={{ color: "white" }} />
                    ) : (
                        "Importar"
                    )}
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
};

export default ImportContactsModal;
